import { useRef, useState, type RefCallback, type UIEvent } from "react";
import { capabilities, type CapabilityKey } from "./investorPortalCapabilities";

const SECTION_HEADING_HEIGHT = 100;
// Safari 17 has no scrollend event, so a scroll counts as finished once no scroll event arrives for this long.
const SCROLL_IDLE_MS = 150;

const getFirstSectionKeyWithHeadingInView = (sections: Map<CapabilityKey, HTMLElement>, container: HTMLElement) => {
  const { top: containerTop, bottom: containerBottom } = container.getBoundingClientRect();
  let scrolledPastKey: CapabilityKey | undefined;
  for (const { key } of capabilities) {
    const section = sections.get(key);
    if (!section) {
      continue;
    }
    const sectionTop = section.getBoundingClientRect().top;
    if (sectionTop >= containerTop - 1) {
      return sectionTop <= containerBottom - SECTION_HEADING_HEIGHT ? key : (scrolledPastKey ?? key);
    }
    scrolledPastKey = key;
  }
  return scrolledPastKey;
};

const getActiveCapabilityKey = (sections: Map<CapabilityKey, HTMLElement>, container: HTMLElement) => {
  // On a tall screen the previous section's heading can still be in view at the bottom, so the bottom selects the last one.
  if (container.scrollTop + container.clientHeight >= container.scrollHeight - 1) {
    return capabilities.at(-1)?.key;
  }
  return getFirstSectionKeyWithHeadingInView(sections, container);
};

interface CapabilityScrollSpy {
  activeKey: CapabilityKey;
  handleScroll: (e: UIEvent<HTMLDivElement>) => void;
  selectCapability: (key: CapabilityKey) => void;
  registerSection: (key: CapabilityKey) => RefCallback<HTMLElement>;
}

const useCapabilityScrollSpy = (): CapabilityScrollSpy => {
  const [activeKey, setActiveKey] = useState<CapabilityKey>("documents");
  const sectionsRef = useRef(new Map<CapabilityKey, HTMLElement>());
  const scrollTrackingPauseTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const pauseScrollTrackingUntilIdle = () => {
    clearTimeout(scrollTrackingPauseTimerRef.current);
    scrollTrackingPauseTimerRef.current = setTimeout(() => {
      scrollTrackingPauseTimerRef.current = undefined;
    }, SCROLL_IDLE_MS);
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    if (scrollTrackingPauseTimerRef.current !== undefined) {
      pauseScrollTrackingUntilIdle();
      return;
    }

    const capabilityKey = getActiveCapabilityKey(sectionsRef.current, e.currentTarget);
    if (capabilityKey) {
      setActiveKey(capabilityKey);
    }
  };

  const selectCapability = (key: CapabilityKey) => {
    setActiveKey(key);
    pauseScrollTrackingUntilIdle();
    sectionsRef.current.get(key)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const registerSection =
    (key: CapabilityKey): RefCallback<HTMLElement> =>
    (section) => {
      if (!section) {
        return;
      }
      sectionsRef.current.set(key, section);
      return () => {
        sectionsRef.current.delete(key);
      };
    };

  return { activeKey, handleScroll, selectCapability, registerSection };
};

export default useCapabilityScrollSpy;
